// What the shopper chooses or adds to a product before buying it, kept on the cart line:
// - sizes and colours (clothing; gift stores call the sizes something else, product.optionLabel, e.g. "Mug size")
// - personalisation (gift stores, product.personalization): a name or message (text) and their own photos.
// The same product with a different pick is a different line. Products without any of these work as before, and their
// lines are still known by the product id alone.

export const sizesOf = (p) => (p && Array.isArray(p.sizes) ? p.sizes : []);
export const colorsOf = (p) => (p && Array.isArray(p.colors) ? p.colors : []);
export const personalOf = (p) => (p && p.personalization && (p.personalization.text || p.personalization.photos > 0) ? p.personalization : null);
// What the sizes are called on this product: "Size" unless the owner named them ("Mug size", "Frame size")
export const optionLabelOf = (p) => (p && p.optionLabel) || "Size";
// The product at one of its sizes' price (L 1000, M 500): a size without its own price costs the product's price, and
// the struck-out price is the size's own original price, or keeps the product's discount (the backend's
// utils/variants.js sizePricing)
export const pricedAt = (p, size) => {
    const own = size && (p.sizePrices || []).find((x) => x.size.toLowerCase() === String(size).toLowerCase());
    if (!own) return p;
    if (own.originalPrice > 0) return { ...p, price: own.price, originalPrice: own.originalPrice };
    const ratio = p.price > 0 && p.originalPrice > p.price ? p.originalPrice / p.price : 1;
    return { ...p, price: own.price, originalPrice: Math.round(own.price * ratio) };
};
// The price to show: the picked size's, or the cheapest size's with from: true when the sizes cost different amounts
export const shownPrice = (p, size) => {
    if (size && sizesOf(p).includes(size)) return { ...pricedAt(p, size), from: false };
    const all = sizesOf(p).map((s) => pricedAt(p, s));
    if (new Set(all.map((x) => x.price)).size < 2) return { ...(all[0] || p), from: false };
    return { ...all.reduce((low, x) => (x.price < low.price ? x : low)), from: true };
};
export const needsPick = (p) => sizesOf(p).length > 0 || colorsOf(p).length > 0 || Boolean(personalOf(p));

// True when the pick has everything the product asks for (a required message or photo included)
export const pickComplete = (p, pick = {}) => {
    const personal = personalOf(p);
    const required = personal && personal.required;
    return (
        (!sizesOf(p).length || sizesOf(p).includes(pick.size)) &&
        (!colorsOf(p).length || colorsOf(p).some((c) => c.name === pick.color)) &&
        (!required || !personal.text || Boolean((pick.text || "").trim())) &&
        (!required || !personal.photos || (pick.photos || []).length > 0)
    );
};

// The cart line's key: the product id, or the product with everything picked for it
export const lineKey = (item) =>
    item.size || item.color || item.text || (item.photos && item.photos.length)
        ? `${item.id}|${item.size || ""}|${item.color || ""}|${item.text || ""}|${(item.photos || []).join(",")}`
        : item.id;

// How many of a product are in the cart, over all its picks
export const qtyInCart = (cart, p) => cart.filter((item) => item.id === p.id).reduce((sum, item) => sum + item.qty, 0);

// "Size M · Pink" / "11 oz · “Happy Birthday Riya” · 1 photo" for a cart line or order item
export const pickLabel = (item) => {
    const photos = Array.isArray(item.photos) ? item.photos.length : 0;
    return [
        item.size && (/^\d/.test(item.size) || item.size.length > 4 ? item.size : `Size ${item.size}`),
        item.color,
        item.text && `“${item.text}”`,
        photos && `${photos} photo${photos === 1 ? "" : "s"}`,
    ].filter(Boolean).join(" · ");
};

// Asks the store to open the size/colour picker for a product (components/VariantSheet.js listens). A product to
// personalise opens its own page instead, where there is room for the message and photos.
export const askForPick = (product) => window.dispatchEvent(new CustomEvent("gn:pick", { detail: product }));
